import { useState, useCallback } from "react";
import { useMutation } from "@apollo/client";
import { REQUEST_UPLOAD_URL, CONFIRM_UPLOAD } from "../graphql/storage";
import { useLocale } from "../context/LanguageContext";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

type UploadPurpose = "AVATAR" | "ORGANIZATION" | "EVENT" | "CHALLENGE" | "MARKETPLACE";

interface UseImageUploadOptions {
  purpose: UploadPurpose;
}

interface UseImageUploadReturn {
  upload: (file: File, entityId: string) => Promise<string>;
  uploading: boolean;
  error: string | null;
  reset: () => void;
}

export function useImageUpload({
  purpose,
}: UseImageUploadOptions): UseImageUploadReturn {
  const { t } = useLocale();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [requestUploadUrl] = useMutation(REQUEST_UPLOAD_URL);
  const [confirmUpload] = useMutation(CONFIRM_UPLOAD);

  const reset = useCallback(() => {
    setError(null);
    setUploading(false);
  }, []);

  const upload = useCallback(
    async (file: File, entityId: string): Promise<string> => {
      setError(null);
      setUploading(true);

      try {
        if (!ALLOWED_TYPES.includes(file.type)) {
          throw new Error(t("adminUI.imageUploader.errorFileType"));
        }
        if (file.size > MAX_FILE_SIZE) {
          throw new Error(t("adminUI.imageUploader.errorFileSize"));
        }

        // 1. Get presigned upload URL
        const { data: urlData } = await requestUploadUrl({
          variables: { purpose, entityId, contentType: file.type },
        });
        const { uploadUrl, key } = urlData.requestUploadUrl;

        // 2. PUT file to S3
        const putResponse = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        if (!putResponse.ok) {
          throw new Error(t("adminUI.imageUploader.uploadFailed"));
        }

        // 3. Confirm upload
        const { data: confirmData } = await confirmUpload({
          variables: { key },
        });

        return confirmData.confirmUpload.cdnUrl;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : t("adminUI.imageUploader.uploadFailed");
        setError(message);
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [purpose, requestUploadUrl, confirmUpload, t],
  );

  return { upload, uploading, error, reset };
}
